import { PropTypes } from 'prop-types';
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { Menu } from '@ge/components/menu';
import { Radio } from '@ge/components/radio';
import { Title } from '@ge/components/typography';
import { BacklogSortingValue, BacklogSortingLabel, SortDirection } from '@ge/models/constants';
import { killEventPropagation } from '@ge/shared/util/general';

import { PlanningContext, DefaultBacklogSort } from '../../../context/planning-provider';

const SortMenu = styled.div``;

const SortBtn = styled.div`
  border-radius: 2px;
  background: ${(props) =>
    props.active ? props.theme.select.primaryBackground : props.theme.select.secondaryBackground};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 111px;
  border: solid 1px ${({ theme }) => theme.select.borderColor};

  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;

  &:hover {
    border: solid 1px #9bd9e4;
  }
  .chevronDown {
    margin: 0 6px 0 1px;
  }
`;

const SortIcon = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.SORT,
  color: props.theme.filterPanel.iconColor,
  viewbox: '0 0 12 12',
}))`
  padding-left: 6px;
`;

const ArrowUpIcon = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.ARROW_UP,
  color: props.isActive
    ? props.theme.filterPanel.iconColor
    : props.theme.dataExplorer.dialog.arrowColor,
  viewbox: '0 0 14.66 22',
}))``;

const ArrowDownIcon = styled(Icon).attrs((props) => ({
  size: 12,
  icon: Icons.ARROW_DOWN,
  color: props.isActive
    ? props.theme.filterPanel.iconColor
    : props.theme.dataExplorer.dialog.arrowColor,
  viewbox: '0 0 14.66 22',
  className: 'arrowDown',
}))``;

// eslint-disable-next-line react/prop-types
const ArrowSortIcon = ({ onClick, direction }) => (
  <div onClick={onClick} className="arrowSortIcon">
    <ArrowUpIcon isActive={direction === SortDirection.ASC} />
    <ArrowDownIcon isActive={direction === SortDirection.DESC} />
  </div>
);

const Text = styled.span`
  font-size: 12px;
  line-height: 10px;
  color: ${(props) => props.theme.manage.filterMenu.color};
  padding: 4px 0px 4px 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 66px;
`;

const StyledImg = styled(Icon).attrs((props) => ({
  size: 10,
  icon: Icons.CHEVRON,
  className: 'chevronDown',
  rotate: props.open ? 180 : 0,
  color: '#8694a1',
}))``;

const StyledContent = styled.div`
  > .title {
    padding-left: 28px;
    padding-top: 12px;
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .footer {
    padding: 6px;
    border-top: solid 1px ${(props) => props.theme.manage.filterMenu.borderColor};
    display: flex;
    justify-content: flex-end;
    justify-content: space-between;
    background: ${(props) => props.theme.manage.filterMenu.background};
  }
  max-height: 450px;
  overflow: auto;
  &::-webkit-scrollbar {
    width: 4px;
    border-radius: 1px;
  }
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.scrollbar.trackBackground};
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.scrollbar.thumbBackground};
    border-radius: 2.5px;
  }
`;

const StyledSortList = styled.div`
  padding-left: 28px;
  padding-bottom: 12px;
`;

export const BacklogSort = ({ state }) => {
  const { t } = useTranslation(['tasks', 'general', 'manage.planning', 'analyze.dashboard'], {
    useSuspense: false,
  });

  const {
    planningState: { backlogSort, handleSetBacklogSort },
  } = useContext(PlanningContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [backLogSortState, setBacklogSortState] = useState(backlogSort);

  // const { approvalFlag } = useStoreState((state) => state.tenant.featureFlags);

  const showMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setBacklogSortState(backlogSort);
  };

  const handleApply = (newState = backLogSortState) => {
    handleSetBacklogSort(newState);
    handleMenuClose();
  };

  const handleClear = () => {
    setAnchorEl(null);
    handleSetBacklogSort(DefaultBacklogSort);
  };

  /**
   * @param {*} updateValue Value to update
   * @param {'metric'|'direction'} updateKey Key to update
   */
  const handleChange = (updateValue, updateKey) => {
    const newState = {
      ...backLogSortState,
      [updateKey]: updateValue,
    };

    setBacklogSortState(newState);
    return newState;
  };

  const buildRadio = ({ value, label }) => {
    return (
      <Radio
        id={value}
        name={'Sort-Selection'}
        label={label}
        checked={backLogSortState.metric === value}
        onClick={() => handleChange(value, 'metric')}
        key={value}
      />
    );
  };

  useEffect(() => {
    // track the filter context and update the local filter state to re-check the checkboxes on menu open.
    setBacklogSortState(backlogSort);
  }, [backlogSort, state]);

  const sourceSortOptions = Object.keys(BacklogSortingValue).map((key) => ({
    label: t(
      BacklogSortingLabel[BacklogSortingValue[key]].label,
      BacklogSortingLabel[BacklogSortingValue[key]].labelValue,
    ),
    value: BacklogSortingValue[key],
  }));

  return (
    <>
      <SortMenu onClick={(e) => killEventPropagation(e)}>
        <SortBtn onClick={(e) => showMenu(e)}>
          <SortIcon />
          <Text>
            {t(
              BacklogSortingLabel[backlogSort.metric].label,
              BacklogSortingLabel[backlogSort.metric].labelValue,
            )}
          </Text>
          <StyledImg open={Boolean(anchorEl)} />
        </SortBtn>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} zIndex={101}>
          <StyledContent>
            <Title level={3} className="title">
              {t('general:sort_by', 'Sort By')}
            </Title>
            <StyledSortList>
              {sourceSortOptions?.map((option) => buildRadio(option))}
            </StyledSortList>
            <div className="footer">
              <Button onClick={handleClear}>{t('general:cancel', 'Cancel')}</Button>
              <Button primary onClick={() => handleApply()}>
                {t('general:apply', 'Apply')}
              </Button>
            </div>
          </StyledContent>
        </Menu>
      </SortMenu>
      <ArrowSortIcon
        onClick={() => {
          const newState = handleChange(
            backLogSortState.direction === SortDirection.ASC
              ? SortDirection.DESC
              : SortDirection.ASC,
            'direction',
          );
          handleApply(newState);
        }}
        direction={backLogSortState.direction}
      />
    </>
  );
};

BacklogSort.propTypes = {
  state: PropTypes.string,
};

BacklogSort.defaultProps = {
  state: 'backlog',
};
